import React, { useState, useEffect, useRef } from 'react';
import { List, ListItem, ListItemIcon, Divider, TextField, AppBar, Toolbar, IconButton, InputAdornment, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ChatIcon from '@material-ui/icons/Chat';
import Person from '@material-ui/icons/Person';
import CircularProgress from '@material-ui/core/CircularProgress';
import SendIcon from '@material-ui/icons/Send';
import TableChartIcon from '@material-ui/icons/TableChart';
import './ChatWindow.css'; // Import CSS file
import { parseWindowsOS } from '../../helpers';

function ChatWindow({isFetching, messages, state, getAIAssistants, onClose}) {
  const [newMessage, setNewMessage] = useState('');
  const endOfMessagesRef = useRef(null);

  const scrollToBottom = () => {
    if (endOfMessagesRef.current) {
      endOfMessagesRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = () => {
    if (!newMessage.trim() || isFetching) return;
    getAIAssistants(newMessage, newMessage);
    setNewMessage('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && event.shiftKey) {
      // event.preventDefault();
      // setNewMessage(newMessage + '\n');
      // const cursorPosition = event.target.selectionStart;
      // const beforeCursor = newMessage.slice(0, cursorPosition);
      // const afterCursor = newMessage.slice(cursorPosition);
      // setNewMessage(beforeCursor + "\n" + afterCursor);

      // setTimeout(() => {
      //   if (event.target) {
      //     event.target.selectionStart = event.target.selectionEnd = cursorPosition + 1;
      //   }
      // }, 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleTerminalCSV = (items, filter) => {
    const filteredData = items.filter(item => 
      (filter.checkedFilter === false || item.statusKb !== "2") &&
      (!filter.user_name || item.user_name.includes(filter.user_name)) &&
      (!filter.section_name || item.section_name.includes(filter.section_name)) &&
      (!filter.department_name || item.department_name.includes(filter.department_name)) &&
      (!filter.pc_name || item.pc_name.includes(filter.pc_name))
    );
    const kStatusName = ["利用中", "保管", "廃棄"];
    const csvHeaderTitles = ["管理番号", "端末番号", "状態", "コメント", "ユーザー名", "型番", "モデル名", "シリアルNo", "CPU情報", "メモリ", "OS", "使用期間", "利用年数"];
    const csvRows = [csvHeaderTitles.join(',')];

    filteredData.forEach(item => {
      const row = [
        item.systemNo,
        item.pc_name,
        kStatusName[item.statusKb], 
        item.memo || '', 
        item.user_name,
        item.manufacturer,
        item.system_model,
        item.serial_number,
        item.cpu_info,
        Number(item.mem_installed).toFixed(2),
        item.os_name || parseWindowsOS(item.os_info),
        item.used_Period,
        item.used_yearCount === null || item.used_yearCount.includes('0000/00/00') ? '不明' : item.used_yearCount,
      ];
      csvRows.push(row.map(v => `"${v.toString().replace(/"/g, '""')}"`).join(','));
    });

    // CSVファイル説明
    const replaceMessage = [];
    const columnsName = [];
    csvHeaderTitles.forEach((item, index) => {
      columnsName.push(`${index+1}列名が${item}である。`);
    })
    replaceMessage.push('＊＊＊＊「管理番号一覧」のCSVデータを送信しました＊＊＊＊');
    replaceMessage.push('これは、当社の端末利用実績である。' + columnsName.join(''));
    replaceMessage.push('このテーブルの内容を元に、次の問い合わせに回答ください。');
    
    const csvData = csvRows.concat(replaceMessage).join('\n');
    // setNewMessage(csvData);
    getAIAssistants(csvData, replaceMessage.join('\n'));
  };

  const handleUserCSV = (items, filter) => {
    const filteredData = items.filter(item =>
      (filter.user_name === '-1' || filter.user_name === item.user_name) &&
      (filter.section_name === '-1' || filter.section_name === item.section_name) &&
      (filter.department_name === '-1' || filter.department_name === item.department_name) &&
      (filter.pc_name === '-1' || filter.pc_name === item.pc_name)
    );
    const csvHeaderTitles = ["ユーザー名", "部", "課", "前回終了時間", "最新起動時間", "端末番号", "型番", "モデル名", "シリアルNo", "利用年数"];
    const csvRows = [csvHeaderTitles.join(',')];
  
    filteredData.forEach(item => {
      const row = [
        item.user_name,
        item.section_name,
        item.department_name,
        item.last_shutdown_time,
        item.start_time,
        item.pc_name,
        item.manufacturer,
        item.system_model,
        item.serial_number,
        item.used_yearCount.includes('0000/00/00') ? '不明' : item.used_yearCount
      ];
      csvRows.push(row.map(v => `"${v.toString().replace(/"/g, '""')}"`).join(','));
    });

    // CSVファイル説明
    const replaceMessage = [];
    const columnsName = [];
    csvHeaderTitles.forEach((item, index) => {
      columnsName.push(`${index+1}列名が${item}である。`);
    })
    replaceMessage.push('＊＊＊＊「ユーザー一覧」のCSVデータを送信しました＊＊＊＊');
    replaceMessage.push('これは、当社のユーザー登録実績である。' + columnsName.join(''));
    replaceMessage.push('このテーブルの内容を元に、次の問い合わせに回答ください。');
  
    const csvData = csvRows.concat(replaceMessage).join('\n');
    // setNewMessage(csvData);
    getAIAssistants(csvData, replaceMessage.join('\n'));
  }

  const handlePCSpecCSV = (items, filter) => {
    const filteredData = items.filter(item =>
      (filter.user_name === '-1' || filter.user_name === item.user_name) &&
      (filter.hpa27_version === '-1' || filter.hpa27_version === item.hpa27_version) &&
      (filter.pc_name === '-1' || filter.pc_name === item.pc_name)
    );
    const csvHeaderTitles = ["端末番号", "ユーザー名", "バージョン", "型番", "モデル名", "シリアルNo"];
    const csvRows = [csvHeaderTitles.join(',')];
  
    filteredData.forEach(item => {
      const row = [
        item.pc_name,
        item.user_name,
        item.hpa27_version,
        item.manufacturer,
        item.system_model,
        item.serial_number
      ];
      csvRows.push(row.join(','));
    });
  
    // CSVファイル説明
    const replaceMessage = [];
    const columnsName = [];
    csvHeaderTitles.forEach((item, index) => {
      columnsName.push(`${index+1}列名が${item}である。`);
    })
    replaceMessage.push('＊＊＊＊「導入一覧一覧」のCSVデータを送信しました＊＊＊＊');
    replaceMessage.push('これは、当社の端末導入実績である。' + columnsName.join(''));
    replaceMessage.push('このテーブルの内容を元に、次の問い合わせに回答ください。');

    const csvData = csvRows.concat(replaceMessage).join('\n');
    // setNewMessage(csvData);
    getAIAssistants(csvData, replaceMessage.join('\n'));
  }

  const handleTableIconClick = () => {
    const search = window.location.search;
    const query = new URLSearchParams(search);
    const p = query.get('p');
    console.log(p);
    const { terminal, user, pcspec } = state;
    if (p === 'terminal') {
      handleTerminalCSV(terminal.items.detailData, terminal.filter);
    } else if (p === 'user') {
      handleUserCSV(user.items.detailData, user.filter);
    } else if (p === 'pcspec') {
      handlePCSpecCSV(pcspec.items.detailData, pcspec.filter);
    }
  };

  return (
    <div className="chatContainer">
      <AppBar position="static">
        <Toolbar variant="dense">
          {/* <h6 style={{ flexGrow: 1, textAlign: 'center' }}>AIアシスタント</h6> */}
          <Typography variant="h6" color="inherit" style={{ flexGrow: 1, textAlign: 'center' }}>AIアシスタント</Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <List className="chatSection">
        {messages.map((msg, index) => (
          <React.Fragment key={index}>
            <ListItem>
              <ListItemIcon>
                {index % 2 === 0 ? <Person /> : <ChatIcon />}
              </ListItemIcon>
              <div className="messageContent">{msg}</div>
            </ListItem>
            {index % 2 === 0 ? <Divider component="li" variant="inset" /> : <Divider component="li" />}
          </React.Fragment>
        ))}
        <div ref={endOfMessagesRef} />
      </List>
      {isFetching && (<div className="wait-response"><CircularProgress /></div>)}
      <div className="sendForm">
        <TextField
          placeholder="プロンプト入力..."
          variant="outlined"
          size="small"
          multiline
          maxRows={4}
          className="sendInput"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          // onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleSendMessage}>
                  <SendIcon />
                </IconButton>
                <IconButton onClick={handleTableIconClick}>
                  <TableChartIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>
    </div>
  );
}

export default ChatWindow;
